import React, { useContext, useEffect, useState, Fragment } from "react";
import { AuthContext } from "../contexts/auth";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import CustomTooltipComponent from "./custom_tooltip_component";
import FilePreviewModal from "../components/preview_modal";
import Swal from "sweetalert2";
import {
  companyVehiclesList,
  companyVehicleCreate,
  companyVehiclePatch,
  companyVehicleDelete,
} from "../services/api_vehicle_employee";
import { Listbox, Transition } from '@headlessui/react';

const DEFAULT_FUEL_CHOICES = [
  ["benzina", "Benzina"],
  ["diesel", "Diesel"],
  ["gpl", "GPL"],
  ["metano", "Metano"],
  ["ibrida", "Ibrida"],
  ["elettrica", "Elettrica"],
  ["altro", "Altro"],
];

const extractFieldErrors = (err) => {
  const data = err?.response?.data;
  if (!data || typeof data !== "object") return {};
  const fe = {};
  Object.entries(data).forEach(([k, v]) => {
    if (Array.isArray(v)) fe[k] = v.join(" ");
    else if (typeof v === "string") fe[k] = v;
  });
  return fe;
};

const formatFieldErrorsHTML = (fe) =>
  `<ul style="text-align:left;margin:0;padding-left:1rem;">
    ${Object.entries(fe)
      .map(([f, msg]) => `<li><b>${f}</b>: ${msg}</li>`)
      .join("")}
   </ul>`;

export default function VeicoliTabCompany({
  employeeId,
  readOnly = false,
  canEditRate = true,
  fuelChoices = DEFAULT_FUEL_CHOICES,
}) {
  const { token } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [error, setError] = useState(null);

  const [openId, setOpenId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editId, setEditId] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [previewDoc, setPreviewDoc] = useState(null);

  const [form, setForm] = useState({
    license_plate: "",
    vehicle_brand: "",
    vehicle_type: "",
    vehicle_motor: "",
    rate_per_km: "",
    file: null,
  });
  const [formErrors, setFormErrors] = useState({});

  const [editForm, setEditForm] = useState({
    license_plate: "",
    vehicle_brand: "",
    vehicle_type: "",
    vehicle_motor: "",
    rate_per_km: "",
    file: undefined, // undefined=non toccare; null=rimuovi; File=aggiorna
  });
  const [editErrors, setEditErrors] = useState({});

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  const fetchAll = async () => {
    if (!token || !employeeId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await companyVehiclesList(token, {
        employee: employeeId,
        page: 1,
        pageSize: 50,
      });
      setVehicles(Array.isArray(res?.results) ? res.results : []);
    } catch (err) {
      console.error(err);
      setError("Impossibile caricare i veicoli.");
      await Swal.fire({
        icon: "error",
        title: "Errore",
        text: "Impossibile caricare i veicoli.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // reset quando cambia dipendente
    setVehicles([]);
    setError(null);
    setOpenId(null);
    setShowAdd(false);
    setEditId(null);
    if (token && employeeId) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, employeeId]);

  const resetForm = () => {
    setForm({
      license_plate: "",
      vehicle_brand: "",
      vehicle_type: "",
      vehicle_motor: "",
      rate_per_km: "",
      file: null,
    });
    setFormErrors({});
  };

  const handlePlateBlur = (val, setFn) =>
    setFn((prev) => ({
      ...prev,
      license_plate: (val || "").trim().toUpperCase(),
    }));

  const validateCreate = () => {
    const errs = {};
    if (!form.license_plate?.trim()) errs.license_plate = "La targa è obbligatoria.";
    if (!form.vehicle_brand?.trim()) errs.vehicle_brand = "La marca è obbligatoria.";
    if (!form.vehicle_type?.trim()) errs.vehicle_type = "Il modello è obbligatorio.";
    if (
      form.vehicle_motor &&
      !fuelChoices.map(([v]) => v).includes(form.vehicle_motor)
    ) {
      errs.vehicle_motor = "Valore non valido.";
    }
    if (canEditRate && form.rate_per_km !== "" && form.rate_per_km !== null) {
      const n = Number(form.rate_per_km);
      if (Number.isNaN(n) || n < 0)
        errs.rate_per_km = "Inserisci un valore numerico valido (≥ 0).";
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (!validateCreate()) {
      await Swal.fire({
        icon: "error",
        title: "Dati mancanti",
        text: "Controlla i campi evidenziati.",
      });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        license_plate: form.license_plate.trim().toUpperCase(),
        vehicle_brand: form.vehicle_brand.trim(),
        vehicle_type: (form.vehicle_type || "").trim(),
        vehicle_motor: (form.vehicle_motor || "") || null,
        ...(canEditRate
          ? {
              rate_per_km:
                form.rate_per_km !== "" && form.rate_per_km !== null
                  ? Number(form.rate_per_km)
                  : null,
            }
          : {}),
        registration_document: form.file || undefined,
        employee: employeeId, // <-- obbligatorio per la create company
      };
      const created = await companyVehicleCreate(token, payload);
      setVehicles((prev) => [created, ...prev]);
      resetForm();
      setShowAdd(false);

      await Swal.fire({
        icon: "success",
        title: "Salvato",
        text: "Veicolo creato correttamente.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      const fe = extractFieldErrors(err);
      if (Object.keys(fe).length) {
        await Swal.fire({
          icon: "error",
          title: "Controlla i campi",
          html: formatFieldErrorsHTML(fe),
        });
      } else {
        await Swal.fire({
          icon: "error",
          title: "Errore",
          text: "Errore durante il salvataggio.",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vehicleId) => {
    const result = await Swal.fire({
      title: 'Sei sicuro?',
      text: "Stai eliminando il veicolo e l'operazione non è reversibile. Confermi l'eliminazione?",
      icon: 'warning',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Sì, confermo',
      cancelButtonText: 'No, annulla',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await companyVehicleDelete(token, vehicleId);

      await Swal.fire({
        title: 'Eliminato!',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      setVehicles((prev) => prev.filter((v) => v.id !== vehicleId));
      if (openId === vehicleId) setOpenId(null);
      if (editId === vehicleId) setEditId(null);
    } catch (error) {
      let errorMessage = "Errore durante l'eliminazione.";

      const data = error?.response?.data;
      if (data) {
        if (typeof data === 'string') {
          errorMessage = data;
        }
        else if (Array.isArray(data)) {
          errorMessage = data.join('\n');
        }
        else if (typeof data === 'object') {
          if (Object.prototype.hasOwnProperty.call(data, 'detail')) {
            const d = data.detail;
            errorMessage = Array.isArray(d) ? d.join('\n') : String(d);
          } else {
            const msgs = [];
            Object.entries(data).forEach(([key, value]) => {
              if (Array.isArray(value)) msgs.push(value.join(', '));
              else if (typeof value === 'string') msgs.push(value);
              else msgs.push(JSON.stringify(value));
            });
            if (msgs.length) errorMessage = msgs.join('\n');
          }
        }
      }

      await Swal.fire('Errore', errorMessage, 'error');
    }
  };

  const collectAttachments = (v) => {
    const out = [];
    const doc = v?.registration_document || v?.file;
    if (!doc) return out;

    if (typeof doc === "string") {
      const name = doc.split("/").pop() || "allegato";
      out.push({ id: "registration_document", name, url: doc });
    } else if (typeof doc === "object" && doc.url) {
      out.push({
        id: "registration_document",
        name: doc.name || "allegato",
        url: doc.url,
        mime: doc.mime,
      });
    }
    return out;
  };

  const startEdit = (v) => {
    setEditErrors({});
    setEditId(v.id);
    setEditForm({
      license_plate: v.license_plate || "",
      vehicle_brand: v.vehicle_brand || "",
      vehicle_type: v.vehicle_type || "",
      vehicle_motor: v.vehicle_motor || "",
      rate_per_km:
        v.rate_per_km === null || v.rate_per_km === undefined
          ? ""
          : String(v.rate_per_km),
      file: undefined,
    });
    if (openId !== v.id) setOpenId(v.id);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditErrors({});
    setEditForm({
      license_plate: "",
      vehicle_brand: "",
      vehicle_type: "",
      vehicle_motor: "",
      rate_per_km: "",
      file: undefined,
    });
  };

  const validateEdit = () => {
    const errs = {};
    if (!editForm.license_plate?.trim()) errs.license_plate = "La targa è obbligatoria.";
    if (!editForm.vehicle_brand?.trim()) errs.vehicle_brand = "La marca è obbligatoria.";
    if (!editForm.vehicle_type?.trim()) errs.vehicle_type = "Il modello è obbligatorio.";
    if (
      editForm.vehicle_motor &&
      !fuelChoices.map(([v]) => v).includes(editForm.vehicle_motor)
    ) {
      errs.vehicle_motor = "Valore non valido.";
    }
    if (canEditRate && editForm.rate_per_km !== "" && editForm.rate_per_km !== null) {
      const n = Number(editForm.rate_per_km);
      if (Number.isNaN(n) || n < 0)
        errs.rate_per_km = "Inserisci un valore numerico valido (≥ 0).";
    }
    setEditErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const saveEdit = async (e, v) => {
    e?.preventDefault?.();
    if (!validateEdit()) {
      await Swal.fire({
        icon: "error",
        title: "Dati mancanti",
        text: "Controlla i campi evidenziati.",
      });
      return;
    }

    try {
      const payload = {};

      // --- rileva cambio tariffa (considera "" = null) ---
      let rateChanged = false;
      let parsedRate = null;
      if (canEditRate) {
        const next = editForm.rate_per_km === "" ? null : Number(editForm.rate_per_km);
        const prev = v.rate_per_km == null ? null : Number(v.rate_per_km);
        rateChanged = (next ?? null) !== (prev ?? null);
        parsedRate = next; // lo riuso sotto
      }
      // se è cambiata la tariffa, chiedi conferma
      if (rateChanged) {
        const { isConfirmed } = await Swal.fire({
          icon: "warning",
          title: "Confermi la modifica della tariffa?",
          text: "L'importo delle richieste di rimborsi spese collegate al veicolo, che utilizzano il valore di default, verrà ricalcolato. Confermi?",
          showCancelButton: true,
          confirmButtonText: "Conferma",
          cancelButtonText: "Annulla",
        });
        if (!isConfirmed) return;
      }
      setSavingEdit(true);

      if (editForm.license_plate !== v.license_plate)
        payload.license_plate = editForm.license_plate.trim().toUpperCase();
      if (editForm.vehicle_brand !== v.vehicle_brand)
        payload.vehicle_brand = editForm.vehicle_brand.trim();
      if (editForm.vehicle_type !== v.vehicle_type)
        payload.vehicle_type = (editForm.vehicle_type || "").trim();
      if (editForm.vehicle_motor !== v.vehicle_motor)
        payload.vehicle_motor = (editForm.vehicle_motor || "") || null;

      if (canEditRate) {
        const prev = v.rate_per_km == null ? null : Number(v.rate_per_km);
        if ((parsedRate ?? null) !== (prev ?? null)) payload.rate_per_km = parsedRate;
      }

      if (editForm.file !== undefined) payload.registration_document = editForm.file;

      const updated = await companyVehiclePatch(token, v.id, payload);
      setVehicles((prev) => prev.map((x) => (x.id === v.id ? updated : x)));
      cancelEdit();

      await Swal.fire({
        icon: "success",
        title: "Aggiornato",
        text: "Veicolo aggiornato correttamente.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      const fe = extractFieldErrors(err);
      if (Object.keys(fe).length) {
        await Swal.fire({
          icon: "error",
          title: "Controlla i campi",
          html: formatFieldErrorsHTML(fe),
        });
      } else {
        await Swal.fire({
          icon: "error",
          title: "Errore",
          text: "Errore durante l’aggiornamento.",
        });
      }
    } finally {
      setSavingEdit(false);
    }
  };

  const openPreview = (url, name, mime) => {
    if (!url) return;
    setPreviewDoc({ url, name, mime });
  };

  // se manca l’employeeId mostro uno stato neutro
  if (!employeeId) {
    return (
      <div className="p-6 text-sm text-gray-600">
        Nessun dipendente selezionato.
      </div>
    );
  }

  return (
    <div>
      {!readOnly && (
        <div className="pt-2 pb-4">
          <button
            type="button"
            onClick={() => setShowAdd((s) => !s)}
            className="relative inline-flex items-center rounded-sm bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            {showAdd ? "Annulla" : "Aggiungi Veicolo"}
          </button>
        </div>
      )}

      {!readOnly && showAdd && (
        <form
          onSubmit={handleCreate}
          className="bg-white border border-gray-200 rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3"
        >
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Targa *</label>
            <input
              className={`w-full border rounded px-2 py-1 text-sm uppercase tracking-wide ${
                formErrors.license_plate ? "border-red-500" : ""
              }`}
              value={form.license_plate}
              onChange={(e) => setForm((f) => ({ ...f, license_plate: e.target.value }))}
              onBlur={(e) => handlePlateBlur(e.target.value, setForm)}
              placeholder="AB123CD"
              aria-invalid={!!formErrors.license_plate}
              required
            />
            {formErrors.license_plate && (
              <p className="mt-1 text-xs text-red-600">{formErrors.license_plate}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Marca *</label>
            <input
              className={`w-full border rounded px-2 py-1 text-sm ${
                formErrors.vehicle_brand ? "border-red-500" : ""
              }`}
              value={form.vehicle_brand}
              onChange={(e) => setForm((f) => ({ ...f, vehicle_brand: e.target.value }))}
              placeholder="Fiat, Ford, Tesla…"
              aria-invalid={!!formErrors.vehicle_brand}
              required
            />
            {formErrors.vehicle_brand && (
              <p className="mt-1 text-xs text-red-600">{formErrors.vehicle_brand}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Modello *</label>
            <input
              className={`w-full border rounded px-2 py-1 text-sm ${
                formErrors.vehicle_type ? "border-red-500" : ""
              }`}
              value={form.vehicle_type}
              onChange={(e) => setForm((f) => ({ ...f, vehicle_type: e.target.value }))}
              placeholder="Panda, Golf…"
              aria-invalid={!!formErrors.vehicle_type}
              required
            />
            {formErrors.vehicle_type && (
              <p className="mt-1 text-xs text-red-600">{formErrors.vehicle_type}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Alimentazione</label>

            <Listbox
              value={form.vehicle_motor || ""}
              onChange={(val) => setForm((f) => ({ ...f, vehicle_motor: val }))}
            >
              {({ open }) => (
                <div className="relative">
                  {/* Button */}
                  <Listbox.Button
                    aria-invalid={!!formErrors.vehicle_motor}
                    className={[
                      "w-full rounded border px-2 py-1 text-sm text-left shadow-sm focus:outline-none",
                      formErrors.vehicle_motor ? "border-red-500" : "border-gray-300",
                    ].join(" ")}
                  >
                    <span className="block truncate">
                      {form.vehicle_motor
                        ? (fuelChoices.find(([v]) => v === form.vehicle_motor)?.[1] || form.vehicle_motor)
                        : "— Seleziona —"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                      <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </Listbox.Button>

                  {/* Options */}
                  <Transition
                    show={open}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options
                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1
                                text-sm shadow-lg ring-1 ring-black/10 focus:outline-none"
                    >
                      {/* Opzione vuota */}
                      <Listbox.Option
                        key="__empty__"
                        value=""
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-3 pr-8 ${
                            active ? "bg-indigo-50 text-indigo-900" : "text-gray-900"
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                              — Seleziona —
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 right-2 flex items-center text-indigo-600">✓</span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>

                      {fuelChoices.map(([value, label]) => (
                        <Listbox.Option
                          key={value}
                          value={value}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-3 pr-8 ${
                              active ? "bg-indigo-50 text-indigo-900" : "text-gray-900"
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                {label}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 right-2 flex items-center text-indigo-600">✓</span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              )}
            </Listbox>

            {formErrors.vehicle_motor && (
              <p className="mt-1 text-xs text-red-600">{formErrors.vehicle_motor}</p>
            )}
          </div>

          {canEditRate && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Tariffa €/km</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={`w-full border rounded px-2 py-1 text-sm ${
                  formErrors.rate_per_km ? "border-red-500" : ""
                }`}
                value={form.rate_per_km}
                onChange={(e) => setForm((f) => ({ ...f, rate_per_km: e.target.value }))}
                placeholder="0.35"
                aria-invalid={!!formErrors.rate_per_km}
              />
              {formErrors.rate_per_km && (
                <p className="mt-1 text-xs text-red-600">{formErrors.rate_per_km}</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Allegato (libretto)
            </label>
            <input
              type="file"
              accept=".pdf,image/*"
              className="w-full text-sm"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setForm((prev) => ({ ...prev, file: f || null }));
              }}
            />
            {formErrors.registration_document && (
              <p className="mt-1 text-xs text-red-600">{formErrors.registration_document}</p>
            )}
          </div>

          <div className="md:col-span-3 flex gap-2 mt-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center rounded-sm bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowAdd(false);
              }}
              className="inline-flex items-center rounded-sm bg-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 hover:bg-gray-300"
            >
              Annulla
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg overflow-visible pt-2">
        {loading ? (
          <div className="p-6 text-sm text-gray-600">Caricamento…</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : (
          <table className="min-w-full">
            <thead className="border-b border-gray-200">
              <tr className="bg-white text-sm font-semibold text-gray-900">
                <th className="pb-2 text-left" />
                <th className="pb-2 text-left">Targa</th>
                <th className="pb-2 text-left">Marca</th>
                <th className="pb-2 text-left" />
              </tr>
            </thead>

            <tbody className="bg-white">
              {vehicles.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-sm text-gray-500 italic">
                    Nessun veicolo salvato
                  </td>
                </tr>
              )}

              {vehicles.map((v) => {
                const files = collectAttachments(v);

                return (
                  <Fragment key={v.id}>
                    <tr
                      className="border-t cursor-pointer hover:bg-gray-50 overflow-visible"
                      onClick={() => toggle(v.id)}
                    >
                      <td className="py-3 w-8 px-2">
                        {openId === v.id ? (
                          <ChevronUpIcon className="w-5 h-5" />
                        ) : (
                          <ChevronDownIcon className="w-5 h-5" />
                        )}
                      </td>

                      <td className="py-3 text-sm font-medium">
                        <span className="font-semibold tracking-wide uppercase">
                          {v.license_plate || "—"}
                        </span>
                      </td>

                      <td className="py-3 text-sm">
                        <span className="text-gray-700">{v.vehicle_brand || "—"}</span>
                      </td>

                      <td className="py-3 pr-3">
                        <div className="flex items-center justify-end gap-2">
                          {files[0]?.url && (
                            <CustomTooltipComponent title="Apri anteprima">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPreview(files[0].url, "Allegato", files[0].mime);
                                }}
                                className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
                              >
                                <MagnifyingGlassIcon className="h-5 w-5" />
                              </button>
                            </CustomTooltipComponent>
                          )}

                          {!readOnly && (
                            <CustomTooltipComponent title="Modifica veicolo">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEdit(v);
                                }}
                                className="inline-flex items-center text-gray-700 hover:text-indigo-700"
                              >
                                <PencilSquareIcon className="h-5 w-5" />
                              </button>
                            </CustomTooltipComponent>
                          )}

                          {!readOnly && (
                            <CustomTooltipComponent title="Elimina veicolo">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(v.id);
                                }}
                                className="inline-flex items-center text-gray-700 hover:text-red-600"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </CustomTooltipComponent>
                          )}
                        </div>
                      </td>
                    </tr>

                    {openId === v.id && (
                      <tr className="bg-gray-50 shadow-lg relative z-10">
                        <td className="py-0 w-8 px-2" />
                        <td colSpan={3} className="pr-4 py-4 text-sm relative z-10">
                          <dl className="grid grid-cols-12 gap-y-1 items-center">
                            <dt className="col-span-3 font-semibold">Targa</dt>
                            <dd className="col-span-9 uppercase">{v.license_plate || "—"}</dd>

                            <dt className="col-span-3 font-semibold">Marca</dt>
                            <dd className="col-span-9">{v.vehicle_brand || "—"}</dd>

                            {v.vehicle_type && (
                              <>
                                <dt className="col-span-3 font-semibold">Modello</dt>
                                <dd className="col-span-9">{v.vehicle_type}</dd>
                              </>
                            )}

                            {v.vehicle_motor && (
                              <>
                                <dt className="col-span-3 font-semibold">Alimentazione</dt>
                                <dd className="col-span-9 capitalize">{v.vehicle_motor}</dd>
                              </>
                            )}

                            <dt className="col-span-3 font-semibold">Tariffa €/km</dt>
                            <dd className="col-span-9">
                              {v.rate_per_km !== null && v.rate_per_km !== undefined
                                ? Number(v.rate_per_km).toLocaleString("it-IT", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                : "—"}
                            </dd>

                            {files[0]?.url && (
                              <>
                                <dt className="col-span-3 font-semibold">Allegato</dt>
                                <dd className="col-span-9">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openPreview(files[0].url, "Allegato", files[0].mime);
                                    }}
                                    className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
                                  >
                                    <MagnifyingGlassIcon className="h-4 w-4" />
                                    Visualizza
                                  </button>
                                </dd>
                              </>
                            )}
                          </dl>

                          {!readOnly && editId === v.id && (
                            <form
                              onSubmit={(e) => saveEdit(e, v)}
                              className="mt-4 border-t pt-4 grid grid-cols-1 md:grid-cols-3 gap-3"
                            >
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                  Targa *
                                </label>
                                <input
                                  className={`w-full border rounded px-2 py-1 text-sm uppercase ${
                                    editErrors.license_plate ? "border-red-500" : ""
                                  }`}
                                  value={editForm.license_plate}
                                  onChange={(e) =>
                                    setEditForm((f) => ({ ...f, license_plate: e.target.value }))
                                  }
                                  onBlur={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      license_plate: (e.target.value || "")
                                        .trim()
                                        .toUpperCase(),
                                    }))
                                  }
                                  aria-invalid={!!editErrors.license_plate}
                                />
                                {editErrors.license_plate && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {editErrors.license_plate}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                  Marca *
                                </label>
                                <input
                                  className={`w-full border rounded px-2 py-1 text-sm ${
                                    editErrors.vehicle_brand ? "border-red-500" : ""
                                  }`}
                                  value={editForm.vehicle_brand}
                                  onChange={(e) =>
                                    setEditForm((f) => ({ ...f, vehicle_brand: e.target.value }))
                                  }
                                  aria-invalid={!!editErrors.vehicle_brand}
                                />
                                {editErrors.vehicle_brand && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {editErrors.vehicle_brand}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                  Modello *
                                </label>
                                <input
                                  className={`w-full border rounded px-2 py-1 text-sm ${
                                    editErrors.vehicle_type ? "border-red-500" : ""
                                  }`}
                                  value={editForm.vehicle_type}
                                  onChange={(e) =>
                                    setEditForm((f) => ({ ...f, vehicle_type: e.target.value }))
                                  }
                                  aria-invalid={!!editErrors.vehicle_type}
                                />
                                {editErrors.vehicle_type && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {editErrors.vehicle_type}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                  Alimentazione
                                </label>
                                <div className="relative">
                                  <Listbox
                                    value={editForm.vehicle_motor ?? ""}
                                    onChange={(val) => setEditForm(f => ({ ...f, vehicle_motor: val }))}
                                  >
                                    {({ open }) => (
                                      <div className="relative">
                                        {/* Button */}
                                        <Listbox.Button
                                          aria-invalid={!!editErrors.vehicle_motor}
                                          className={[
                                            "w-full rounded border px-2 py-1 text-sm text-left shadow-sm focus:outline-none bg-white relative z-20",
                                            editErrors.vehicle_motor ? "border-red-500" : "border-gray-300",
                                          ].join(" ")}
                                        >
                                          <span className="block truncate">
                                            {editForm.vehicle_motor
                                              ? (fuelChoices.find(([v]) => v === editForm.vehicle_motor)?.[1] ?? editForm.vehicle_motor)
                                              : "— Seleziona —"}
                                          </span>
                                          <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                            <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                              <path
                                                fillRule="evenodd"
                                                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          </span>
                                        </Listbox.Button>

                                        {/* Options */}
                                        <Transition
                                          show={open}
                                          leave="transition ease-in duration-100"
                                          leaveFrom="opacity-100"
                                          leaveTo="opacity-0"
                                        >
                                          <Listbox.Options
                                            className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1
                                                      text-sm shadow-lg ring-1 ring-black/10 focus:outline-none"
                                          >
                                            {/* Opzione vuota */}
                                            <Listbox.Option
                                              key="__empty__"
                                              value=""
                                              className={({ active }) =>
                                                `relative cursor-default select-none py-2 pl-3 pr-8 ${
                                                  active ? "bg-indigo-50 text-indigo-900" : "text-gray-900"
                                                }`
                                              }
                                            >
                                              {({ selected }) => (
                                                <>
                                                  <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                                    — Seleziona —
                                                  </span>
                                                  {selected ? (
                                                    <span className="absolute inset-y-0 right-2 flex items-center text-indigo-600">✓</span>
                                                  ) : null}
                                                </>
                                              )}
                                            </Listbox.Option>

                                            {fuelChoices.map(([value, label]) => (
                                              <Listbox.Option
                                                key={value}
                                                value={value}
                                                className={({ active }) =>
                                                  `relative cursor-default select-none py-2 pl-3 pr-8 ${
                                                    active ? "bg-indigo-50 text-indigo-900" : "text-gray-900"
                                                  }`
                                                }
                                              >
                                                {({ selected }) => (
                                                  <>
                                                    <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                                      {label}
                                                    </span>
                                                    {selected ? (
                                                      <span className="absolute inset-y-0 right-2 flex items-center text-indigo-600">✓</span>
                                                    ) : null}
                                                  </>
                                                )}
                                              </Listbox.Option>
                                            ))}
                                          </Listbox.Options>
                                        </Transition>
                                      </div>
                                    )}
                                  </Listbox>
                                </div>
                                {editErrors.vehicle_motor && (
                                  <p className="mt-1 text-xs text-red-600">{editErrors.vehicle_motor}</p>
                                )}
                              </div>

                              {canEditRate && (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                                    Tariffa €/km
                                  </label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className={`w-full border rounded px-2 py-1 text-sm ${
                                      editErrors.rate_per_km ? "border-red-500" : ""
                                    }`}
                                    value={editForm.rate_per_km}
                                    onChange={(e) =>
                                      setEditForm((f) => ({
                                        ...f,
                                        rate_per_km: e.target.value,
                                      }))
                                    }
                                    aria-invalid={!!editErrors.rate_per_km}
                                  />
                                  {editErrors.rate_per_km && (
                                    <p className="mt-1 text-xs text-red-600">
                                      {editErrors.rate_per_km}
                                    </p>
                                  )}
                                </div>
                              )}

                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                  Sostituisci allegato (opzionale)
                                </label>
                                <input
                                  type="file"
                                  accept=".pdf,image/*"
                                  className="w-full text-sm"
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    setEditForm((prev) => ({
                                      ...prev,
                                      file: f || null, // null=rimuovi; File=nuovo
                                    }));
                                  }}
                                />
                                {editErrors.registration_document && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {editErrors.registration_document}
                                  </p>
                                )}
                              </div>

                              <div className="md:col-span-3 flex gap-2 mt-2">
                                <button
                                  type="submit"
                                  disabled={savingEdit}
                                  className="inline-flex items-center rounded-sm bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                                >
                                  {savingEdit ? "Salvataggio…" : "Salva modifiche"}
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="inline-flex items-center rounded-sm bg-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 hover:bg-gray-300"
                                >
                                  Annulla
                                </button>
                              </div>
                            </form>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <FilePreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </div>
  );
}